import React, { createContext, useState } from 'react';

import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { sumFormFormatter } from '@shared/helpers/sumFormFormatter';
import { CustomModal } from '@shared/ui';

import cls from './AddPaymentModal.module.css';
import { ExtraStep } from './ExtraStep/ExtraStep';
import { FirstStep } from './FirstStep/FirstStep';
import { SecondStep } from './SecondStep/SecondStep';
import { AddPartnerModal } from '../AddPartnerModal/AddPartnerModal';

export const ModalContext = createContext({});
export const AddPaymentModal = ({
    mode = 'add',
    payment = null,
    isOpen,
    onShowModal,
    step,
    modalState,
    onHideModalPartner,
    onHideModal,
    setModalState,
    onSwitcherChange,
    deleteUndistributedPayment,
}) => {
    const { t } = useTranslation();

    const { accountsSelectList, partnersSelectList, archiveAccountList } = useSelector(
        (state) => state.paymentReducer
    );
    const defaultAccount = accountsSelectList.find((account) => account.account.default_account);
    let initAccount;

    if (payment) {
        initAccount = !payment.account.is_archive ? payment.account.guid : defaultAccount.value;
    } else {
        initAccount = defaultAccount.value;
    }

    let initPartner = null;

    if (payment && payment.correspondent) {
        initPartner = payment.correspondent.guid;
    }
    if (modalState.addPaymentMode === 'applications' && !!payment.company_partner) {
        initPartner = payment.company_partner.guid;
    }

    let paymentDateInit = payment
        ? moment(payment.payment_date).format('DD.MM.YYYY')
        : moment(new Date()).format('DD.MM.YYYY');
    let accrualDateInit = payment
        ? moment(payment.accrual_date).format('DD.MM.YYYY')
        : moment(new Date()).format('DD.MM.YYYY');

    if (mode === 'repeat') {
        paymentDateInit = moment(new Date()).format('DD.MM.YYYY');
        accrualDateInit = moment(new Date()).format('DD.MM.YYYY');
    }

    const expendituresInit =
        payment && payment.type !== 'transfer' ? payment?.payment_item?.guid : null;

    let firstStepAccountInit = defaultAccount.value;

    if (payment && payment.correspondent) {
        firstStepAccountInit =
            payment.correspondent.title !== undefined
                ? payment.correspondent.guid
                : defaultAccount.value;

        if (firstStepAccountInit) {
            const isArchive = archiveAccountList.some(
                (account) => account.value === firstStepAccountInit
            );
            firstStepAccountInit = isArchive ? defaultAccount.value : payment.correspondent.guid;
        }
    }

    const sumInit = payment ? sumFormFormatter(String(payment.sum)) : '0';
    const receiveSumInit = payment ? sumFormFormatter(String(payment.sum)) : '0';

    let purposePaymentInit = payment ? payment.target : '';

    if (modalState.addPaymentMode === 'applications') {
        purposePaymentInit = payment.payment_item.title;
    }

    const commentInit = payment ? payment.comment : '';

    let estimateInit = null;

    if (payment && payment.sum_divisions.length) {
        if (payment.sum_divisions[0].project.length === 0) {
            estimateInit = null;
        } else if (payment.sum_divisions[0].estimate.id !== undefined) {
            estimateInit = payment.sum_divisions[0].estimate.guid;
        } else {
            estimateInit = payment.sum_divisions[0].project.id;
        }
    }

    let distributeEstimatesInit = [];

    if (payment && payment.sum_divisions.length > 1) {
        const dis = payment.sum_divisions.map((estimate, i) => {
            let estimateSelect;

            if (estimate.project.length === 0) {
                estimateSelect = null;
            } else if (estimate.estimate.id !== undefined) {
                estimateSelect = estimate.estimate.guid;
            } else {
                estimateSelect = estimate.project.id;
            }

            const id = Date.now() + i;
            const { sum } = estimate;
            const percent = (Number(sum) / Number(payment.sum)) * 100;

            return {
                estimateSelect,
                id,
                sum,
                percent,
            };
        });

        distributeEstimatesInit = dis;
    }

    let innInit = '';
    let kppInit = '';
    let bikInit = '';
    let accountNumberInit = '';
    let fullNameInit = '';

    if (payment && payment.payload) {
        innInit = payment.payload.inn;
        kppInit = payment.payload.kpp;
        bikInit = payment.payload.bik;
        accountNumberInit = payment.payload.accountNumber;
        fullNameInit = payment.payload.name;
    }

    const [formState, setFormState] = useState({
        account: initAccount,
        partners: initPartner,
        estimates: estimateInit,
        expenditures: expendituresInit,
        firstStepAccount: firstStepAccountInit,
        paymentDate: paymentDateInit,
        accrualDate: accrualDateInit,
        sum: sumInit,
        receiveSum: receiveSumInit,
        inn: innInit,
        kpp: kppInit,
        bik: bikInit,
        accountNumber: accountNumberInit,
        fullName: fullNameInit,
        purposePayment: purposePaymentInit,
        purposeName: '',
        comment: commentInit,
        invoicePayment: [],
        distributeEstimates: distributeEstimatesInit,
    });

    const [errors, setErrors] = useState({
        account: null,
        sum: null,
        paymentItem: null,
        estimate: null,
        correspondent: null,
        receiverAccount: null,
        purposePayment: null,
        fullName: null,
        bik: null,
        accountNumber: null,
        receiveSum: null,
    });

    const [isFirstRenderDebit, setIsFirstRenderDebit] = useState(false);

    const inputHandler = (event) => {
        const { name, value } = event.target;

        setFormState((prevState) => ({ ...prevState, [name]: value }));
    };

    const sumFormHandler = (event) => {
        const { name, value } = event.target;

        const formattedValue = sumFormFormatter(value);

        setFormState((prevState) => ({
            ...prevState,
            [name]: formattedValue,
        }));
    };

    const handleIsFirstRenderDebit = () => {
        setIsFirstRenderDebit(true);
    };

    const selectedAccount = accountsSelectList.find(
        (account) => account.value === formState.account
    );

    const selectedFirstStepAccount = accountsSelectList.find(
        (account) => account.value === formState.firstStepAccount
    );

    const selectedPartners = partnersSelectList.find(
        (partner) => partner.value === formState.partners
    );

    const onPaymentDateChange = (newDate) => {
        const date = moment(new Date(newDate)).utc().format('DD.MM.YYYY');
        setFormState((prevState) => ({ ...prevState, paymentDate: date }));
    };

    const onAccrualDateChange = (newDate) => {
        const date = moment(new Date(newDate)).utc().format('DD.MM.YYYY');
        setFormState((prevState) => ({ ...prevState, accrualDate: date }));
    };
    const selectAccountHandler = (value) => {
        setFormState((prevState) => ({ ...prevState, account: value }));
    };
    const selectPartnersHandler = (value) => {
        setFormState((prevState) => ({ ...prevState, partners: value }));
    };
    const selectEstimateHandler = (value) => {
        setFormState((prevState) => ({ ...prevState, estimates: value }));
    };
    const selectExpenditureHandler = (value) => {
        setFormState((prevState) => ({ ...prevState, expenditures: value }));
    };
    const selectFirstStepAccountHandler = (value) => {
        setFormState((prevState) => ({ ...prevState, firstStepAccount: value }));
    };

    const [needUpdateExtraData, setNeedUpdateExtraData] = useState(true);

    const modalContextValue = {
        formState,
        setFormState,
        selectedAccount,
        selectAccountHandler,
        selectedPartners,
        selectedFirstStepAccount,
        selectEstimateHandler,
        selectFirstStepAccountHandler,
        selectPartnersHandler,
        selectExpenditureHandler,
        inputHandler,
        onPaymentDateChange,
        onAccrualDateChange,
        errors,
        setErrors,
        mode,
        sumFormHandler,
        needUpdateExtraData,
        setNeedUpdateExtraData,
        handleIsFirstRenderDebit,
        isFirstRenderDebit,
    };

    const isDistribution = mode === 'distribution';
    const isCanDeleteUnsettled = !!payment?.is_can_delete_unsettled && isDistribution;

    const modeMapping = {
        1: (
            <FirstStep
                deleteUndistributedPayment={deleteUndistributedPayment}
                isCanDeleteUnsettled={isCanDeleteUnsettled}
                modalState={modalState}
                setModalState={setModalState}
                onSwitcherChange={onSwitcherChange}
                onHideModal={onHideModal}
            />
        ),
        2: (
            <SecondStep
                deleteUndistributedPayment={deleteUndistributedPayment}
                isCanDeleteUnsettled={isCanDeleteUnsettled}
                modalState={modalState}
                setModalState={setModalState}
                onHideModal={onHideModal}
                accountsSelectList={accountsSelectList}
                payment={payment}
            />
        ),
        3: (
            <ExtraStep
                deleteUndistributedPayment={deleteUndistributedPayment}
                isCanDeleteUnsettled={isCanDeleteUnsettled}
                onHideModal={onHideModal}
                modalState={modalState}
                accountsSelectList={accountsSelectList}
                setModalState={setModalState}
                payment={payment}
            />
        ),
    };

    const modeMappingRepeat = {
        distribution: (
            <CustomModal.Header>
                {t('AddPaymentModal.modeMappingRepeat.distribution')}
            </CustomModal.Header>
        ),
        repeat: (
            <CustomModal.Header>
                {t('AddPaymentModal.modeMappingRepeat.repeat')} №{payment ? payment.number : ''}
            </CustomModal.Header>
        ),
    };

    return (
        <ModalContext.Provider value={modalContextValue}>
            <CustomModal contentClasses={cls.modal} isOpen={isOpen} onHideModal={onShowModal}>
                {mode === 'add' ? (
                    <CustomModal.Header>{t('AddPaymentModal.return.add')}</CustomModal.Header>
                ) : (
                    <>{modeMappingRepeat[mode]}</>
                )}

                {modeMapping[step]}
            </CustomModal>
            {modalState.isOpenPartners && (
                <AddPartnerModal
                    isOpen={modalState.isOpenPartners}
                    onShowModal={onHideModalPartner}
                    setMainForm={setFormState}
                />
            )}
        </ModalContext.Provider>
    );
};
